<template>
  <v-container class="py-4" max-width="960">
    <!-- ─── Setup Phase ─── -->
    <v-card v-if="!matchId" variant="outlined" class="mb-4">
      <v-card-title class="d-flex align-center">
        <v-icon class="mr-2">mdi-dice-multiple</v-icon>
        New match
      </v-card-title>
      <v-divider />
      <v-card-text>
        <v-row>
          <v-col cols="12" sm="6">
            <v-text-field v-model="setup.player1" label="Player 1" variant="outlined"
              density="compact" />
          </v-col>
          <v-col cols="12" sm="6">
            <v-text-field v-model="setup.player2" label="Player 2" variant="outlined"
              density="compact" />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" sm="6">
            <v-select v-model="setup.matchType" :items="matchTypes" label="Match type"
              variant="outlined" density="compact" />
          </v-col>
          <v-col cols="12" sm="6" v-if="setup.matchType === 'match'">
            <v-select v-model="setup.matchLength" :items="matchLengths" label="Match length"
              variant="outlined" density="compact" />
          </v-col>
        </v-row>
        <v-btn color="primary" variant="flat" @click="createNewMatch" :loading="loading">
          Start new match
        </v-btn>
      </v-card-text>
    </v-card>

    <!-- ─── Active Match ─── -->
    <template v-if="matchId && state">
      <!-- Score bar -->
      <v-card variant="outlined" class="mb-3">
        <v-card-text class="d-flex align-center justify-space-between py-2">
          <div class="text-center flex-grow-1">
            <div class="text-body-2 text-medium-emphasis">{{ state.player1 }}</div>
            <div class="text-h5 font-weight-bold">{{ state.score?.scores?.[0] || 0 }}</div>
          </div>
          <div class="text-center px-4">
            <v-chip v-if="state.score?.match_length" size="small" variant="tonal">
              Kamp til {{ state.score.match_length }}
            </v-chip>
            <v-chip v-else size="small" variant="tonal">Moneygame</v-chip>
            <div v-if="state.cube?.value > 1" class="mt-1">
              <v-chip size="small" color="warning" variant="flat">
                Terning: {{ state.cube.value }}
                <span v-if="state.cube.owner"> (S{{ state.cube.owner }})</span>
              </v-chip>
            </div>
            <div v-if="state.score?.is_crawford" class="mt-1">
              <v-chip size="x-small" color="error" variant="flat">Crawford</v-chip>
            </div>
          </div>
          <div class="text-center flex-grow-1">
            <div class="text-body-2 text-medium-emphasis">{{ state.player2 }}</div>
            <div class="text-h5 font-weight-bold">{{ state.score?.scores?.[1] || 0 }}</div>
          </div>
        </v-card-text>
      </v-card>

      <!-- Match over -->
      <v-alert v-if="state.phase === 'match_over'" type="success" variant="tonal" class="mb-3">
        <strong>Match over!</strong>
        {{ state.score.winner === 1 ? state.player1 : state.player2 }} wins
        {{ state.score.scores[0] }}-{{ state.score.scores[1] }}
      </v-alert>

      <!-- Game over - start next -->
      <v-alert v-if="state.phase === 'game_over'" type="info" variant="tonal" class="mb-3">
        <div class="d-flex align-center justify-space-between">
          <span>Game {{ state.game_number }} over. Score: {{ state.score.scores[0] }}-{{ state.score.scores[1] }}</span>
          <v-btn size="small" variant="flat" color="primary" @click="doStartGame" :loading="loading">
            Start next game
          </v-btn>
        </div>
      </v-alert>

      <!-- Setup - start first game -->
      <v-alert v-if="state.phase === 'setup'" type="info" variant="tonal" class="mb-3">
        <div class="d-flex align-center justify-space-between">
          <span>Klar til at starte</span>
          <v-btn size="small" variant="flat" color="primary" @click="doStartGame" :loading="loading">
            Start game 1
          </v-btn>
        </div>
      </v-alert>

      <!-- ─── Input Area ─── -->
      <v-card v-if="state.phase === 'cube_phase' || state.phase === 'move_phase'"
              variant="outlined" class="mb-3">
        <v-card-text>
          <!-- Current player indicator -->
          <div class="d-flex align-center mb-3">
            <v-chip :color="state.current_player === 1 ? 'blue' : 'red'" variant="flat" size="small">
              S{{ state.current_player }}
            </v-chip>
            <span class="ml-2 font-weight-medium">
              {{ state.current_player === 1 ? state.player1 : state.player2 }}s tur
            </span>
            <span class="ml-2 text-medium-emphasis">
              — Spil {{ state.game_number }}, træk {{ state.turn_number + 1 }}
            </span>
          </div>

          <!-- Cube phase: double or roll -->
          <div v-if="state.phase === 'cube_phase'" class="d-flex align-center ga-3 flex-wrap">
            <span class="text-body-2">Dices:</span>
            <v-btn v-for="d1 in 6" :key="'d1-'+d1"
              :variant="dice[0] === d1 ? 'flat' : 'outlined'"
              :color="dice[0] === d1 ? 'primary' : undefined"
              size="small" min-width="40" @click="dice[0] = d1">
              {{ d1 }}
            </v-btn>
            <span class="mx-1">—</span>
            <v-btn v-for="d2 in 6" :key="'d2-'+d2"
              :variant="dice[1] === d2 ? 'flat' : 'outlined'"
              :color="dice[1] === d2 ? 'primary' : undefined"
              size="small" min-width="40" @click="dice[1] = d2">
              {{ d2 }}
            </v-btn>

            <v-spacer />

            <v-btn color="primary" variant="flat" :disabled="!dice[0] || !dice[1]"
              :loading="loading" @click="doRoll">
              Slå
            </v-btn>

            <v-btn v-if="canDouble" color="warning" variant="outlined"
              :loading="loading" @click="doDouble">
              Dobl
            </v-btn>
          </div>

          <!-- Double response -->
          <div v-if="awaitingDoubleResponse" class="d-flex align-center ga-3">
            <span class="font-weight-medium">
              {{ state.current_player === 1 ? state.player1 : state.player2 }} double!
            </span>
            <v-btn color="success" variant="flat" :loading="loading"
              @click="doRespondDouble(true)">Accept</v-btn>
            <v-btn color="error" variant="flat" :loading="loading"
              @click="doRespondDouble(false)">Decline</v-btn>
          </div>

          <!-- Move phase: select a move -->
          <div v-if="state.phase === 'move_phase'">
            <div class="text-body-2 mb-2">
              Dices: <strong>{{ state.current_dice?.[0] }}-{{ state.current_dice?.[1] }}</strong>
              — Select move:
            </div>

            <div v-if="legalMoves.length === 0" class="mb-3">
              <v-chip color="warning" variant="tonal">Ingen lovlige træk</v-chip>
              <v-btn class="ml-3" size="small" variant="flat" color="primary"
                @click="doMove('Cannot move')" :loading="loading">
                Pas
              </v-btn>
            </div>

            <v-list v-else density="compact" class="mb-2" max-height="300"
              style="overflow-y: auto">
              <v-list-item v-for="(move, i) in legalMoves" :key="i"
                @click="doMove(move)" :value="move"
                class="cursor-pointer">
                <template v-slot:prepend>
                  <span class="text-medium-emphasis mr-2" style="width: 24px">{{ i + 1 }}.</span>
                </template>
                <v-list-item-title class="font-mono">{{ move }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </div>
        </v-card-text>
      </v-card>

      <!-- ─── Move History ─── -->
      <v-card variant="outlined" class="mb-3">
        <v-card-title class="py-2 d-flex align-center">
          <v-icon size="20" class="mr-2">mdi-history</v-icon>
          Trækhistorik
          <v-spacer />
          <v-btn size="x-small" variant="text" @click="showTranscript = !showTranscript">
            {{ showTranscript ? 'Hide' : 'Show' }} transcript
          </v-btn>
        </v-card-title>
        <v-divider />

        <div v-if="showTranscript && transcript" class="pa-3">
          <pre class="text-body-2" style="white-space: pre-wrap; font-family: 'Roboto Mono', monospace">{{ transcript }}</pre>
        </div>

        <v-table v-else density="compact" max-height="250" style="overflow-y: auto">
          <tbody>
            <tr v-for="(entry, i) in moveLog" :key="i"
              :class="{ 'bg-green-lighten-5': i === moveLog.length - 1 }">
              <td style="width: 40px" class="text-medium-emphasis">{{ entry.turn }}</td>
              <td style="width: 60px">
                <v-chip :color="entry.player === 1 ? 'blue' : 'red'" size="x-small" variant="flat">
                  S{{ entry.player }}
                </v-chip>
              </td>
              <td class="font-mono">{{ entry.text }}</td>
            </tr>
          </tbody>
        </v-table>
      </v-card>

      <!-- ─── Actions ─── -->
      <div class="d-flex ga-2 flex-wrap">
        <v-btn v-if="state.phase === 'cube_phase' || state.phase === 'move_phase'"
          size="small" variant="outlined" color="error" @click="showEndDialog = true">
          Afslut spil manuelt
        </v-btn>
        <v-btn v-if="state.phase === 'match_over' || state.phase === 'game_over'"
          size="small" variant="outlined" color="primary" @click="doAnalyze" :loading="analyzing">
          <v-icon class="mr-1" size="16">mdi-brain</v-icon>
          Analysér kamp
        </v-btn>
        <v-spacer />
        <v-btn size="small" variant="text" @click="refreshState" :loading="loading">
          Opdater
        </v-btn>
      </div>

      <!-- Analysis results -->
      <v-card v-if="analysisResult" variant="outlined" class="mt-3">
        <v-card-title class="py-2">
          <v-icon size="20" class="mr-2">mdi-brain</v-icon>
          Analyserapport
        </v-card-title>
        <v-divider />
        <v-card-text>
          <div v-for="game in analysisResult.games" :key="game.game_number" class="mb-4">
            <div class="text-subtitle-2 mb-2">Spil {{ game.game_number }}</div>
            <div class="d-flex ga-4 flex-wrap mb-2">
              <div>
                <span class="text-medium-emphasis">{{ state.player1 }} PR:</span>
                <strong>{{ game.analysis.performance_rating[1]?.toFixed(1) || '—' }}</strong>
              </div>
              <div>
                <span class="text-medium-emphasis">{{ state.player2 }} PR:</span>
                <strong>{{ game.analysis.performance_rating[2]?.toFixed(1) || '—' }}</strong>
              </div>
            </div>
            <div class="d-flex ga-2 flex-wrap">
              <v-chip v-for="cat in ['blunder','very_bad','bad','doubtful']" :key="cat"
                size="x-small" variant="tonal"
                :color="cat === 'blunder' ? 'error' : cat === 'very_bad' ? 'warning' : 'grey'">
                {{ state.player1 }}: {{ game.analysis.error_counts[1]?.[cat] || 0 }} {{ cat }}
              </v-chip>
            </div>
          </div>
        </v-card-text>
      </v-card>
    </template>

    <!-- End game dialog -->
    <v-dialog v-model="showEndDialog" max-width="400">
      <v-card>
        <v-card-title>Afslut spil</v-card-title>
        <v-card-text>
          <v-select v-model="endGameWinner" :items="[
            { title: state?.player1 || 'Player 1', value: 1 },
            { title: state?.player2 || 'Player 2', value: 2 },
          ]" label="Winner" variant="outlined" density="compact" class="mb-3" />
          <v-select v-model="endGameResult" :items="[
            { title: 'Normal', value: 'normal' },
            { title: 'Gammon', value: 'gammon' },
            { title: 'Backgammon', value: 'backgammon' },
          ]" label="Result" variant="outlined" density="compact" />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="showEndDialog = false">Cancel</v-btn>
          <v-btn color="primary" variant="flat" @click="doEndGame" :loading="loading">End game</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Error snackbar -->
    <v-snackbar v-model="showError" color="error" timeout="5000">
      {{ errorMsg }}
    </v-snackbar>
  </v-container>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import {
  createMatch, getMatchState, getMatchMoves, getMatchTranscript,
  startGame, rollDice, makeMove, offerDouble, respondDouble,
  endGame, analyzeMatch,
} from '../services/matchApi';

const route = useRoute();

// Setup
const setup = reactive({
  player1: '',
  player2: '',
  matchType: 'match',
  matchLength: 5,
});

const matchTypes = [
  { title: 'Match', value: 'match' },
  { title: 'Moneygame', value: 'money' },
];
const matchLengths = [1, 3, 5, 7, 9, 11, 13, 15, 17, 21, 25];

// State
const matchId = ref(null);
const state = ref(null);
const legalMoves = ref([]);
const moveLog = ref([]);
const transcript = ref('');
const showTranscript = ref(false);
const dice = reactive([null, null]);
const loading = ref(false);
const analyzing = ref(false);
const showError = ref(false);
const errorMsg = ref('');
const showEndDialog = ref(false);
const endGameWinner = ref(1);
const endGameResult = ref('normal');
const awaitingDoubleResponse = ref(false);
const doublePlayer = ref(null);
const analysisResult = ref(null);

const canDouble = computed(() => {
  if (!state.value || state.value.phase !== 'cube_phase') return false;
  if (state.value.score?.is_crawford) return false;
  const cube = state.value.cube;
  const player = state.value.current_player;
  return cube.owner === null || cube.owner === player;
});

// ─── Actions ─────────────────────────────────────

function showErr(msg) {
  errorMsg.value = msg;
  showError.value = true;
}

async function createNewMatch() {
  loading.value = true;
  try {
    const { data } = await createMatch(
      setup.matchType, setup.matchLength, setup.player1, setup.player2
    );
    matchId.value = data.match_id;
    await refreshState();
  } catch (e) {
    showErr(e.response?.data?.error || e.message);
  }
  loading.value = false;
}

async function doStartGame() {
  loading.value = true;
  try {
    await startGame(matchId.value);
    await refreshState();
    dice[0] = null;
    dice[1] = null;
  } catch (e) {
    showErr(e.response?.data?.error || e.message);
  }
  loading.value = false;
}

async function doRoll() {
  if (!dice[0] || !dice[1]) return;
  loading.value = true;
  try {
    const { data } = await rollDice(matchId.value, state.value.current_player, [dice[0], dice[1]]);
    legalMoves.value = data.legal_moves || [];
    await refreshState();
    if (data.must_pass) {
      // Auto-pass after showing briefly
      setTimeout(() => doMove('Cannot move'), 500);
    }
  } catch (e) {
    showErr(e.response?.data?.error || e.message);
  }
  loading.value = false;
}

async function doMove(notation) {
  loading.value = true;
  try {
    const { data } = await makeMove(matchId.value, state.value.current_player, notation);

    moveLog.value.push({
      turn: state.value.turn_number,
      player: state.value.current_player,
      text: data.notation || notation,
    });

    legalMoves.value = [];
    dice[0] = null;
    dice[1] = null;

    if (data.action === 'game_over') {
      moveLog.value.push({
        turn: '', player: data.winner,
        text: `${data.winner_name} vinder ${data.result} (${data.points_awarded} pt)`,
      });
    }

    await refreshState();
  } catch (e) {
    showErr(e.response?.data?.error || e.message);
  }
  loading.value = false;
}

async function doDouble() {
  loading.value = true;
  try {
    await offerDouble(matchId.value, state.value.current_player);
    doublePlayer.value = state.value.current_player;
    awaitingDoubleResponse.value = true;
    moveLog.value.push({
      turn: state.value.turn_number,
      player: state.value.current_player,
      text: 'Dobler!',
    });
  } catch (e) {
    showErr(e.response?.data?.error || e.message);
  }
  loading.value = false;
}

async function doRespondDouble(takes) {
  loading.value = true;
  try {
    const responder = 3 - doublePlayer.value;
    const { data } = await respondDouble(matchId.value, responder, takes);
    awaitingDoubleResponse.value = false;

    moveLog.value.push({
      turn: state.value.turn_number,
      player: responder,
      text: takes ? 'Tager' : 'Giver op',
    });

    if (data.action === 'game_over') {
      moveLog.value.push({
        turn: '', player: data.winner,
        text: `${data.winner_name} vinder (${data.points_awarded} pt)`,
      });
    }

    await refreshState();
  } catch (e) {
    showErr(e.response?.data?.error || e.message);
  }
  loading.value = false;
}

async function doEndGame() {
  loading.value = true;
  try {
    const { data } = await endGame(matchId.value, endGameWinner.value, endGameResult.value);
    moveLog.value.push({
      turn: '', player: data.winner,
      text: `${data.winner_name} vinder ${data.result} (${data.points_awarded} pt)`,
    });
    showEndDialog.value = false;
    await refreshState();
  } catch (e) {
    showErr(e.response?.data?.error || e.message);
  }
  loading.value = false;
}

async function doAnalyze() {
  analyzing.value = true;
  try {
    const { data } = await analyzeMatch(matchId.value, 2);
    analysisResult.value = data;
  } catch (e) {
    showErr(e.response?.data?.error || e.message);
  }
  analyzing.value = false;
}

async function refreshState() {
  try {
    const { data } = await getMatchState(matchId.value);
    state.value = data;

    // Also refresh transcript
    try {
      const t = await getMatchTranscript(matchId.value);
      transcript.value = t.data?.transcript || t.data || '';
    } catch {}
  } catch (e) {
    showErr(e.response?.data?.error || e.message);
  }
}

onMounted(() => {
  // If navigated to with a matchId in the URL
  if (route.params.matchId) {
    matchId.value = parseInt(route.params.matchId);
    refreshState();
  }
});
</script>

<style scoped>
.font-mono {
  font-family: 'Roboto Mono', monospace;
}
.cursor-pointer {
  cursor: pointer;
}
</style>